/**
 * Pure helpers that classify a drag-and-drop release into a concrete
 * operation (move, copy, no-op, invalid) and build move intents from
 * drag payload data alone.
 */

import type { DraggableData } from "./ids";
import type {
  FavoriteDropItem,
  FavoritesResourceType,
  TransferClassification,
  TransferDestination,
} from "./types";

/** Every destination the per-kind classifiers operate on directly. */
type FileSystemDropDestination = Extract<
  TransferDestination,
  { kind: "workspace" } | { kind: "canvas" } | { kind: "canvas_section" }
>;

type FlowSessionDropDestination = Extract<
  TransferDestination,
  { kind: "flow_session" }
>;

/**
 * Decide what should happen when a drag is released over `destination`.
 * Only depends on the payload data attached to the drag; does not
 * consult any cache.
 *
 * Core rule (D-XWS / D-XCLASS): same class → move, across class → copy.
 * Workspace-class files dropped on any workspace (including cross-workspace)
 * classify as move; non-workspace sources copy.
 */
export function classifyTransfer(
  drag: DraggableData,
  destination: TransferDestination,
): TransferClassification {
  if (destination.kind === "favorites") {
    return classifyFavoritesDrop(drag, destination.resourceType);
  }

  if (destination.kind === "flow_session") {
    return classifyFlowSessionDrop(drag, destination);
  }

  // Home/workspace composer — a chat that may not exist yet, so there is no
  // same-session no-op to detect; files copy, everything else is invalid
  // (mirrors the flow-session rules).
  if (destination.kind === "composer") {
    if (drag.kind === "files") {
      if (drag.payloads.length === 0) {
        return { kind: "noop", reason: "no payload" };
      }
      return { kind: "copy" };
    }
    return { kind: "invalid", reason: "Can't attach this to chat" };
  }

  if (drag.kind === "canvas-card") {
    return classifyCanvasCard(drag, destination);
  }
  if (drag.kind === "section") {
    return classifySection(drag, destination);
  }
  if (drag.kind === "canvas-node") {
    return classifyCanvasNode(drag, destination);
  }
  if (drag.kind === "chat-session") {
    return classifyChatSession(drag, destination);
  }
  if (drag.kind === "integration-connection") {
    return { kind: "invalid", reason: "Connectors can't be dropped here" };
  }
  if (drag.kind === "skill") {
    return { kind: "invalid", reason: "Drag to the canvas board to place" };
  }
  if (drag.kind === "entity") {
    return { kind: "invalid", reason: "Drag to the canvas board to place" };
  }
  return classifyFiles(drag, destination);
}

/** @deprecated Use `classifyTransfer`. */
export const classifyExplorerDrop = classifyTransfer;

function classifyFlowSessionDrop(
  drag: DraggableData,
  destination: FlowSessionDropDestination,
): TransferClassification {
  if (drag.kind === "files") {
    return classifyFilesToFlowSession(drag, destination);
  }
  if (drag.kind === "canvas-card") {
    return { kind: "invalid", reason: "Can't move canvases into chat" };
  }
  if (drag.kind === "entity") {
    return { kind: "invalid", reason: "Drag to the canvas board to place" };
  }
  if (drag.kind === "chat-session") {
    if (drag.payload.flowSessionId === destination.flowSessionId) {
      return { kind: "noop", reason: "already in session" };
    }
    return { kind: "invalid", reason: "Can't move chats between sessions" };
  }
  return { kind: "invalid", reason: "Can't attach this to chat" };
}

function classifyFilesToFlowSession(
  drag: Extract<DraggableData, { kind: "files" }>,
  destination: FlowSessionDropDestination,
): TransferClassification {
  const { payloads } = drag;
  if (payloads.length === 0) {
    return { kind: "noop", reason: "no payload" };
  }

  const allFromSameSession = payloads.every(
    (p) =>
      p.sourceClass === "flow-session" &&
      p.sourceFlowSessionId === destination.flowSessionId,
  );
  if (allFromSameSession) {
    return { kind: "noop", reason: "already in session" };
  }

  return { kind: "copy" };
}

function classifyCanvasCard(
  drag: Extract<DraggableData, { kind: "canvas-card" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  if (destination.kind === "workspace") {
    if (drag.canvasIds.length === 0) {
      return { kind: "noop", reason: "no canvas" };
    }
    // Canvases live in the workspace tree beside files, so any folder in it —
    // including the root — is a legal destination.
    if (
      drag.sourceWorkspaceId === destination.workspaceId &&
      drag.sourcePath === destination.parentPath
    ) {
      // Known to be in this exact folder already. Unknown provenance
      // (`undefined`, multi-canvas drags) is NOT a no-op: the drop runs the
      // move, which is idempotent when it turns out to be redundant.
      return { kind: "noop", reason: "already in folder" };
    }
    return { kind: "move_canvas", workspaceId: destination.workspaceId };
  }

  return { kind: "invalid", reason: "Can't move canvases here" };
}

function classifyChatSession(
  drag: Extract<DraggableData, { kind: "chat-session" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  if (destination.kind === "workspace") {
    // The workspace tree holds folders, canvases and files — never chats.
    // An idle drag over the page background (the detail page wraps its whole
    // surface in a root drop target) reads as a no-op, not an error.
    if (destination.parentPath === "") {
      return { kind: "noop", reason: "chats aren't filed in the tree" };
    }
    return { kind: "invalid", reason: "Chats go in the Chats section" };
  }
  const { sourceCanvasId, flowSessionId, name } = drag.payload;
  if (destination.canvasId !== sourceCanvasId) {
    return { kind: "invalid", reason: "Can't move chats between canvases" };
  }
  return {
    kind: "place_chat",
    canvasId: destination.canvasId,
    flowSessionId,
    name,
    parentSectionId:
      destination.kind === "canvas_section" ? destination.sectionId : null,
  };
}

function classifySection(
  drag: Extract<DraggableData, { kind: "section" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  const { sourceCanvasId, files } = drag.payload;

  if (destination.kind === "workspace") {
    if (files.length === 0) {
      return { kind: "noop", reason: "section has no files" };
    }
    return {
      kind: "section_to_folder",
      workspaceId: destination.workspaceId,
      parentPath: destination.parentPath,
    };
  }

  if (destination.kind === "canvas_section") {
    return { kind: "invalid", reason: "Sections can't be nested" };
  }

  if (destination.canvasId !== sourceCanvasId) {
    return {
      kind: "invalid",
      reason: "Can't move sections between canvases",
    };
  }
  return { kind: "noop", reason: "already at root" };
}

function classifyCanvasNode(
  drag: Extract<DraggableData, { kind: "canvas-node" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  if (destination.kind === "workspace") {
    return { kind: "invalid", reason: "Can't move nodes into workspace" };
  }

  const { nodeId, sourceCanvasId, currentParentSectionId } = drag.payload;
  if (destination.canvasId !== sourceCanvasId) {
    return {
      kind: "invalid",
      reason: "Can't move nodes between canvases",
    };
  }

  if (destination.kind === "canvas") {
    if (currentParentSectionId === null) {
      return { kind: "noop", reason: "already at root" };
    }
    return {
      kind: "reparent_node",
      canvasId: destination.canvasId,
      nodeIds: [nodeId],
      targetParentSectionId: null,
    };
  }

  if (destination.sectionId === currentParentSectionId) {
    return { kind: "noop", reason: "already in this section" };
  }
  return {
    kind: "reparent_node",
    canvasId: destination.canvasId,
    nodeIds: [nodeId],
    targetParentSectionId: destination.sectionId,
  };
}

function classifyFiles(
  drag: Extract<DraggableData, { kind: "files" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  const { payloads } = drag;
  if (payloads.length === 0) {
    return { kind: "noop", reason: "no payload" };
  }

  if (destination.kind === "workspace") {
    const allWorkspaceClass = payloads.every(
      (p) => p.sourceClass === "workspace",
    );
    if (allWorkspaceClass) {
      return {
        kind: "move",
        workspaceId: destination.workspaceId,
        parentPath: destination.parentPath,
      };
    }
    return { kind: "copy" };
  }

  const destCanvasId = destination.canvasId;
  const allFromSameCanvas = payloads.every(
    (p) => p.sourceClass === "canvas" && p.sourceCanvasId === destCanvasId,
  );

  if (!allFromSameCanvas) {
    return { kind: "copy" };
  }

  const allPlaced = payloads.every((p) => p.sourcePlaced !== false);
  const targetSectionId =
    destination.kind === "canvas_section" ? destination.sectionId : null;

  if (!allPlaced) {
    return {
      kind: "place",
      canvasId: destCanvasId,
      parentSectionId: targetSectionId,
    };
  }

  const haveParentInfo = payloads.every(
    (p) => p.sourceParentSectionId !== undefined,
  );

  if (haveParentInfo) {
    const allAlreadyInTarget = payloads.every(
      (p) => (p.sourceParentSectionId ?? null) === targetSectionId,
    );
    if (allAlreadyInTarget) {
      return {
        kind: "noop",
        reason:
          targetSectionId === null
            ? "already at root"
            : "already in this section",
      };
    }
    const movingIds = payloads
      .filter((p) => (p.sourceParentSectionId ?? null) !== targetSectionId)
      .map((p) => p.recordId);
    return {
      kind: "reparent_node",
      canvasId: destCanvasId,
      nodeIds: movingIds,
      targetParentSectionId: targetSectionId,
    };
  }

  if (destination.kind === "canvas") {
    return { kind: "noop", reason: "same canvas" };
  }
  return {
    kind: "reparent_node",
    canvasId: destCanvasId,
    nodeIds: payloads.map((p) => p.recordId),
    targetParentSectionId: targetSectionId,
  };
}

function classifyFavoritesDrop(
  drag: DraggableData,
  resourceType: FavoritesResourceType,
): TransferClassification {
  const items = collectFavoriteDropItems(drag, resourceType);
  if (items === null) {
    return favoriteInvalidReason(resourceType);
  }
  return items.length > 0
    ? { kind: "favorite", items }
    : { kind: "noop", reason: "nothing to favorite" };
}

function collectFavoriteDropItems(
  drag: DraggableData,
  resourceType: FavoritesResourceType,
): FavoriteDropItem[] | null {
  switch (resourceType) {
    case "file-record": {
      if (drag.kind !== "files") return null;
      return drag.payloads
        .filter((p) => p.sourceClass === "workspace" && !p.isFolder)
        .map((p) => ({
          via: "resource-tag" as const,
          resourceType: "file-record" as const,
          resourceId: p.recordId,
        }));
    }
    case "canvas": {
      if (drag.kind !== "canvas-card") return null;
      return drag.canvasIds.map((resourceId) => ({
        via: "resource-tag" as const,
        resourceType: "canvas" as const,
        resourceId,
      }));
    }
    case "skill": {
      if (drag.kind !== "skill") return null;
      return drag.skillIds.map((resourceId) => ({
        via: "resource-tag" as const,
        resourceType: "skill" as const,
        resourceId,
      }));
    }
    case "integration-connection": {
      if (drag.kind !== "integration-connection") return null;
      return drag.connectionIds.map((resourceId) => ({
        via: "resource-tag" as const,
        resourceType: "integration-connection" as const,
        resourceId,
      }));
    }
  }
}

function favoriteInvalidReason(
  resourceType: FavoritesResourceType,
): TransferClassification {
  const reasonByType: Record<FavoritesResourceType, string> = {
    "file-record": "Only files can be favorited here",
    canvas: "Only canvases can be favorited here",
    skill: "Only skills can be favorited here",
    "integration-connection": "Only connectors can be favorited here",
  };
  return { kind: "invalid", reason: reasonByType[resourceType] };
}
